import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";

const HINT = {
  Discovery: "You found each other",
  Proposal: "Accepted proposal or package",
  Negotiation: "NDA signed or contract drafted",
  Approval: "Internal approval workflow approved",
  Contract: "Linked agreement fully signed",
  Payment: "All linked payments paid",
  Activation: "First deliverable completed",
  Reporting: "All deliverables completed",
  Renewal: "Linked agreement renewed",
};

export function DealProgress({ room, onToggle }) {
  const steps = Array.isArray(room?.progress) ? room.progress : [];
  if (!steps.length) return null;
  const doneCount = steps.filter((s) => s.done).length;
  const fill = steps.length > 1 ? ((Math.max(doneCount, 1) - 1) / (steps.length - 1)) * 100 : 0;
  return (
    <div className="mt-8 rounded-2xl border border-[#2A1240]/10 bg-[#FFFFFF] p-6 sm:p-8" data-testid="deal-progress">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="kicker">Deal progress</p>
          <p className="mt-2 font-display text-3xl text-[#2A1240]"><span className="gold-text" data-testid="deal-progress-pct">{room.progress_pct ?? 0}%</span> <span className="text-base text-slate-400">· {doneCount} of {steps.length} stages</span></p>
        </div>
        {steps.find((s) => s.current)?.name === "Approval" && (
          <Link to={`/app/approvals?subject=deal_room&id=${room.id}&title=${encodeURIComponent(room.title)}`} className="btn-ghost !py-2 !px-4 !text-xs" data-testid="deal-progress-request-approval">Request internal approval</Link>
        )}
      </div>

      <div className="mt-8 overflow-x-auto no-scrollbar -mx-2 px-2" data-lenis-prevent>
        <div className="relative min-w-[860px] pb-2">
          <div className="absolute left-[5.5%] right-[5.5%] top-5 h-[2px] bg-[#2A1240]/10" />
          <motion.div className="absolute left-[5.5%] top-5 h-[2px] bg-gradient-to-r from-[#E0218A] to-[#8E2BC4]"
            initial={{ width: 0 }} animate={{ width: `${fill * 0.89}%` }} transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }} />
          <ol className="relative grid grid-cols-9">
            {steps.map((s, i) => {
              const locked = s.auto || s.name === "Discovery";
              return (
                <li key={s.name} className="flex flex-col items-center text-center px-1" data-testid={`deal-progress-${s.name.toLowerCase()}`}>
                  <motion.button type="button" disabled={locked} onClick={() => onToggle?.(s.name)}
                    initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.08 * i, duration: 0.5 }}
                    title={locked ? HINT[s.name] : s.done ? "Click to un-mark" : "Click to mark complete"}
                    className={`relative grid h-10 w-10 place-items-center rounded-full border-2 transition-colors ${s.done ? "bg-[#E0218A] border-[#E0218A] text-white" : s.current ? "bg-[#FFF7FB] border-[#E0218A] text-[#8E2BC4]" : "bg-[#FFF7FB] border-[#2A1240]/20 text-slate-400"} ${locked ? "cursor-default" : "hover:border-[#8E2BC4] cursor-pointer"}`}
                    data-testid={`deal-progress-node-${s.name.toLowerCase()}`}>
                    {s.done ? <Check className="h-5 w-5" strokeWidth={3} /> : <span className="font-mono text-xs">{i + 1}</span>}
                    {s.current && <span className="absolute inset-0 rounded-full border-2 border-[#E0218A] animate-ping opacity-40" />}
                  </motion.button>
                  <p className={`mt-3 text-xs font-medium ${s.done || s.current ? "text-[#2A1240]" : "text-slate-400"}`}>{s.name}</p>
                  <p className="mt-1 text-[11px] leading-tight text-slate-400">
                    {s.done ? (s.auto ? "Done" : "Marked done") : s.in_review ? "In review" : s.current ? "Up next" : ""}
                  </p>
                </li>
              );
            })}
          </ol>
        </div>
      </div>
      <p className="mt-4 text-xs text-slate-400">Stages tick automatically from proposals, NDAs, approvals, contracts, payments and deliverables. You can also tick a stage yourself.</p>
    </div>
  );
}
